/**
 * Lightweight enrichment for ISO 3166-1 alpha-2 country codes. The backend
 * now drives which countries are available per partner (via
 * `claim().config.corridors` and `/widget/catalog/countries`), so this file
 * only carries presentation hints (flag glyph code, default currency) for
 * countries the widget may render.
 *
 * NOTE: keep this map intentionally small. Anything that needs business
 * meaning beyond rendering belongs in the backend's canonical catalog so we
 * have one source of truth across partners.
 */

export interface CountryMeta {
  code: string;
  /** Flag glyph code consumed by `FlagIcon` (kept lower-case). */
  flag: string;
  /**
   * Default ISO 4217 currency used when the backend hasn't returned a
   * currency yet (e.g. an origin row from the claim corridors that hasn't
   * been resolved via `/widget/catalog/countries`).
   */
  defaultCurrency: string;
}

const META: Record<string, CountryMeta> = {
  US: { code: 'US', flag: 'us', defaultCurrency: 'USD' },
  MX: { code: 'MX', flag: 'mx', defaultCurrency: 'MXN' },
  AR: { code: 'AR', flag: 'ar', defaultCurrency: 'ARS' },
  CO: { code: 'CO', flag: 'co', defaultCurrency: 'COP' },
  BR: { code: 'BR', flag: 'br', defaultCurrency: 'BRL' },
  CL: { code: 'CL', flag: 'cl', defaultCurrency: 'CLP' },
  PE: { code: 'PE', flag: 'pe', defaultCurrency: 'PEN' },
  DO: { code: 'DO', flag: 'do', defaultCurrency: 'DOP' },
  GT: { code: 'GT', flag: 'gt', defaultCurrency: 'GTQ' },
  HN: { code: 'HN', flag: 'hn', defaultCurrency: 'HNL' },
  SV: { code: 'SV', flag: 'sv', defaultCurrency: 'USD' },
  NI: { code: 'NI', flag: 'ni', defaultCurrency: 'NIO' },
  CR: { code: 'CR', flag: 'cr', defaultCurrency: 'CRC' },
  PA: { code: 'PA', flag: 'pa', defaultCurrency: 'PAB' },
  EC: { code: 'EC', flag: 'ec', defaultCurrency: 'USD' },
  PY: { code: 'PY', flag: 'py', defaultCurrency: 'PYG' },
  UY: { code: 'UY', flag: 'uy', defaultCurrency: 'UYU' },
  BO: { code: 'BO', flag: 'bo', defaultCurrency: 'BOB' },
  VE: { code: 'VE', flag: 'vez', defaultCurrency: 'VES' },
};

export const countryMeta = (code: string | undefined | null): CountryMeta => {
  if (!code) return { code: '??', flag: 'xx', defaultCurrency: 'USD' };
  return (
    META[code.toUpperCase()] ?? {
      code: code.toUpperCase(),
      flag: code.toLowerCase(),
      defaultCurrency: 'USD',
    }
  );
};

/**
 * Reverse lookup: the representative ISO 3166-1 alpha-2 country code for a
 * given ISO 4217 currency. The backend's operation summary only carries
 * currencies (no country codes), so the home card derives the corridor
 * flags from them. Several countries share `USD`/`EUR`; we pin the most
 * common origin/destination for the widget's corridors. Falls back to the
 * first two letters of the currency code (e.g. `MXN` -> `MX`).
 */
const CURRENCY_TO_COUNTRY: Record<string, string> = {
  USD: 'US',
  MXN: 'MX',
  ARS: 'AR',
  COP: 'CO',
  BRL: 'BR',
  CLP: 'CL',
  PEN: 'PE',
  DOP: 'DO',
  GTQ: 'GT',
  HNL: 'HN',
  NIO: 'NI',
  CRC: 'CR',
  PAB: 'PA',
  PYG: 'PY',
  UYU: 'UY',
  BOB: 'BO',
  VES: 'VE',
};

export const countryForCurrency = (
  currency: string | undefined | null,
): string => {
  if (!currency) return '??';
  const upper = currency.toUpperCase();
  return CURRENCY_TO_COUNTRY[upper] ?? upper.slice(0, 2);
};

/**
 * Cache `Intl.DisplayNames` instances by locale. The constructor isn't cheap
 * (it loads ICU data internally), and we call this once per option per render
 * on the country pickers — without the cache that's a fresh instance per
 * dropdown row on every keystroke that re-renders the parent.
 */
const displayNamesCache = new Map<string, Intl.DisplayNames>();

const getDisplayNames = (locale: string): Intl.DisplayNames => {
  let dn = displayNamesCache.get(locale);
  if (!dn) {
    dn = new Intl.DisplayNames([locale], { type: 'region' });
    displayNamesCache.set(locale, dn);
  }
  return dn;
};

/**
 * Locale-aware country display name for an ISO 3166-1 alpha-2 code.
 *
 * The backend's `/widget/catalog/countries` endpoint only returns destination
 * countries (the origin side — typically `US` — never appears), so we can't
 * rely on it to label the "from" picker. `Intl.DisplayNames` resolves any
 * valid alpha-2 code in the active widget language, no network roundtrip
 * required. Falls back to the uppercased code if the runtime rejects the
 * input (e.g. a non-ISO partner-specific value sneaks in via corridors).
 */
export const countryDisplayName = (
  code: string | undefined | null,
  locale = 'en',
): string => {
  if (!code) return '';
  const upper = code.toUpperCase();
  try {
    return getDisplayNames(locale).of(upper) ?? upper;
  } catch {
    return upper;
  }
};
